import type { HwFile, DetectionEvent } from "../hw/parser";
import {
  COUNTS_TO_MV,
  fmtAreaCounts,
  fmtAreaPercent,
  fmtFwhmSec,
  fmtHeightCounts,
  fmtRetentionMin,
  peakAreaCounts,
  peakAreaPercent,
  peakFwhmSec,
  peakHeightCounts,
  peakRetentionMin,
} from "../hw/parser";
import { tr, trf } from "../i18n";

const nz = (s: string) => (s === "" ? tr("(empty)") : s);

/** Format a Uint8Array as space-separated lowercase hex, optionally
 *  truncating with a `…` suffix. */
function hex(bytes: Uint8Array, max = 64): string {
  const n = Math.min(bytes.length, max);
  const out: string[] = new Array(n);
  for (let i = 0; i < n; i++) {
    out[i] = bytes[i].toString(16).padStart(2, "0");
  }
  return out.join(" ") + (bytes.length > max ? " …" : "");
}

/** Reinterpret a uint32 as a float32 (little-endian on both sides). */
const _f32buf = new ArrayBuffer(4);
const _f32u = new Uint32Array(_f32buf);
const _f32f = new Float32Array(_f32buf);
function u32AsF32(u: number): number {
  _f32u[0] = u >>> 0;
  return _f32f[0];
}
/** Reinterpret a uint32 as an int32. */
function u32AsI32(u: number): number {
  return u | 0;
}

/** High-level "Overview" panel: shows parsed / semantic fields only.
 *  Raw header dwords live in the dedicated Header tab. */
export function InfoTab({ file }: { file: HwFile }) {
  const range = signalRange(file);
  const rows: [string, string][] = [
    [tr("Magic"), trf("%s  (%s)", file.Magic, tr("version") + " " + file.Version)],
    [tr("Magic offset"), String(file.MagicAt)],
    [tr("Sample count"), String(file.SampleCount)],
    [tr("Sampling rate"), trf("%s Hz", file.SamplingHz.toFixed(3))],
    [
      tr("Acquisition time"),
      file.AcquisitionTime
        ? fmtTime(file.AcquisitionTime)
        : tr("(not recorded)"),
    ],
    [tr("Sample name"), nz(file.SampleName)],
    [
      tr("Signal range"),
      range
        ? trf(
            "%s … %s counts  (%s … %s mV)",
            String(range.min),
            String(range.max),
            (range.min * COUNTS_TO_MV).toFixed(3),
            (range.max * COUNTS_TO_MV).toFixed(3),
          )
        : "—",
    ],
    [tr("Amount"), fmtG(file.Header.Amount)],
    [tr("Dilution"), fmtG(file.Header.Dilution)],
    [tr("Components"), String(file.Components.length)],
    [tr("Peaks"), String(file.Peaks.length)],
    [tr("MidRecords"), String(file.MidRecords.length)],
    [tr("TailRecords"), String(file.TailRecords.length)],
    [tr("Audit entries"), String(file.AuditLog.length)],
    [
      tr("Sections"),
      trf(
        "%s sections, %s B total — see Layout tab",
        String(file.Sections.length),
        String(file.Sections.reduce((s, x) => s + x.Length, 0)),
      ),
    ],
  ];
  if (file.AuditLog.length > 0) {
    const first = file.AuditLog[0];
    const last = file.AuditLog[file.AuditLog.length - 1];
    rows.push([
      tr("First audit"),
      `${fmtTime(first.Time)}  ${first.Message}`,
    ]);
    rows.push([
      tr("Last audit"),
      `${fmtTime(last.Time)}  ${last.Message}`,
    ]);
  }
  if (file.ParseWarnings.length > 0) {
    rows.unshift([tr("Parse warnings"), file.ParseWarnings.join("; ")]);
  }
  if (file.ParseError) {
    rows.unshift([tr("Parse error"), file.ParseError]);
  }
  return (
    <table>
      <thead>
        <tr>
          <th>{tr("Field")}</th>
          <th>{tr("Value")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td>{k}</td>
            <td>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function PeaksTab({
  file,
  highlightedPeakIndex,
  onHoverPeak,
}: {
  file: HwFile;
  highlightedPeakIndex?: number | null;
  onHoverPeak?: (index: number | null) => void;
}) {
  if (file.Peaks.length === 0)
    return <div className="empty-state">{tr("(no peaks decoded)")}</div>;
  return (
    <table>
      <thead>
        <tr>
          <th>{tr("#")}</th>
          <th>{tr("RT")}</th>
          <th>{tr("Name")}</th>
          <th>
            {tr("Conc")} <span
              className="column-help"
              title={tr("This is peak area percentage; HW.exe Data View calls it Concentration (Conc).")}
              aria-label={tr("This is peak area percentage; HW.exe Data View calls it Concentration (Conc).")}
              tabIndex={0}
            >i</span>
          </th>
          <th>{tr("Peak area")}</th>
          <th>{tr("Peak height")}</th>
          <th>{tr("FWHM")}</th>
          <th>{tr("Flag")}</th>
        </tr>
      </thead>
      <tbody>
        {file.Peaks.map((p) => (
          <tr
            key={p.Index}
            className={highlightedPeakIndex === p.Index ? "linked-row" : ""}
            onMouseEnter={() => onHoverPeak?.(p.Index)}
            onMouseLeave={() => onHoverPeak?.(null)}
          >
            <td>{p.Index + 1}</td>
            <td>{fmtRetentionMin(peakRetentionMin(p, file.SamplingHz))}</td>
            <td>{p.Name}</td>
            <td>{fmtAreaPercent(peakAreaPercent(p))}</td>
            <td>{fmtAreaCounts(peakAreaCounts(p, file.SamplingHz))}</td>
            <td>{fmtHeightCounts(peakHeightCounts(p))}</td>
            <td>{fmtFwhmSec(peakFwhmSec(p, file.SamplingHz))}</td>
            <td>{p.Flag}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ComponentsTab({ file }: { file: HwFile }) {
  if (file.Components.length === 0)
    return (
      <div className="empty-state">
        {tr("(no calibration components in this file)")}
      </div>
    );
  return (
    <table>
      <thead>
        <tr>
          <th>{tr("#")}</th>
          <th>{tr("RT")}</th>
          <th>{tr("Name")}</th>
          <th>
            {tr("Conc")} <span
              className="column-help"
              title={tr("This is peak area percentage; HW.exe Data View calls it Concentration (Conc).")}
              aria-label={tr("This is peak area percentage; HW.exe Data View calls it Concentration (Conc).")}
              tabIndex={0}
            >i</span>
          </th>
          <th>{tr("Peak area")}</th>
          <th>{tr("Peak height")}</th>
          <th>{tr("FWHM")}</th>
          <th>{tr("Flag")}</th>
        </tr>
      </thead>
      <tbody>
        {file.Components.map((c) => (
          <tr key={c.Index}>
            <td>{c.Index + 1}</td>
            <td>{fmtRetentionMin(peakRetentionMin(c, file.SamplingHz))}</td>
            <td>{c.Name}</td>
            <td>{fmtAreaPercent(peakAreaPercent(c))}</td>
            <td>{fmtAreaCounts(peakAreaCounts(c, file.SamplingHz))}</td>
            <td>{fmtHeightCounts(peakHeightCounts(c))}</td>
            <td>{fmtFwhmSec(peakFwhmSec(c, file.SamplingHz))}</td>
            <td>{c.Flag}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AuditTab({ file }: { file: HwFile }) {
  if (file.AuditLog.length === 0)
    return <div className="empty-state">{tr("(no audit-log entries)")}</div>;
  return (
    <table>
      <thead>
        <tr>
          <th>{tr("Timestamp")}</th>
          <th>{tr("Message")}</th>
        </tr>
      </thead>
      <tbody>
        {file.AuditLog.map((a, i) => (
          <tr key={i}>
            <td>{fmtTime(a.Time)}</td>
            <td>{a.Message}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ReportTab({ file }: { file: HwFile }) {
  return (
    <div className="report-section">
      <p>{trf("Sample name: %s", nz(file.SampleName))}</p>
      <h4>{tr("--- Report header (CString 1) ---")}</h4>
      <pre>{nz(file.ReportText1)}</pre>
      <h4>{tr("--- Report footer (CString 2) ---")}</h4>
      <pre>{nz(file.ReportText2)}</pre>
    </div>
  );
}

export function HeaderTab({ file }: { file: HwFile }) {
  const h = file.Header;
  const rows: [string, string][] = [
    ["U0", String(h.U0)],
    ["U1 (channel?)", String(h.U1)],
    ["U2", String(h.U2)],
    ["U3", String(h.U3)],
    ["U4", String(h.U4)],
    ["U5", String(h.U5)],
    [
      "U6 (TickUS?)",
      // NB: this on-disk field is a stale placeholder in every known file
      // (always 200000). The vendor software ignores it and uses an
      // external HWFrequence.txt instead — see parser.ts DEFAULT_SAMPLING_HZ.
      trf("%d  (ignored — actual: %s Hz)", h.U6, file.SamplingHz.toFixed(3)),
    ],
    ["U7", String(h.U7)],
    ["U8", String(h.U8)],
    ["U9", String(h.U9)],
    ["U10", String(h.U10)],
    ["PreCount", String(h.PreCount)],
    ["PeakCount", String(h.PeakCount)],
    ["MidCount", String(h.MidCount)],
    ["U14", String(h.U14)],
    [
      "AcquisitionTimestamp",
      h.AcquisitionTimestamp > 0
        ? trf(
            "%d  (%s)",
            h.AcquisitionTimestamp,
            new Date(h.AcquisitionTimestamp * 1000).toISOString(),
          )
        : "0",
    ],
    ["U16", String(h.U16)],
    ["U17", String(h.U17)],
    ["U18", String(h.U18)],
    ["U19", String(h.U19)],
    ["U20", String(h.U20)],
    ["U21", String(h.U21)],
    ["U22", fmtG(h.U22)],
    ["Amount", fmtG(h.Amount)],
    ["U24", fmtG(h.U24)],
    ["Dilution", fmtG(h.Dilution)],
  ];
  return (
    <table>
      <thead>
        <tr>
          <th>{tr("Field")}</th>
          <th>{tr("Value")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td>{tr(k)}</td>
            <td>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --- formatting helpers, matching Go fmt verbs used in the original GUI ---

/** The U13 mid-block: 16-byte records sandwiched between Components[] and
 *  Peaks[]. Field semantics unconfirmed (Tier 2). Showing both the
 *  uint32 read of the first 4 bytes and the three trailing float32s, so
 *  consumers can spot which interpretation looks plausible per file. */
export function MidTab({ file }: { file: HwFile }) {
  if (file.MidRecords.length === 0)
    return (
      <div className="empty-state">{tr("(no mid records in this file)")}</div>
    );
  return (
    <table>
      <thead>
        <tr>
          <th>{tr("#")}</th>
          <th title="First 4 bytes read as uint32 — meaning unconfirmed">
            {tr("Word0 (u32)")}
          </th>
          <th title="Bytes 4..16 read as float32 — interpretation unconfirmed">
            {tr("F1 (f32)")}
          </th>
          <th>{tr("F2 (f32)")}</th>
          <th>{tr("F3 (f32)")}</th>
        </tr>
      </thead>
      <tbody>
        {file.MidRecords.map((m, i) => (
          <tr key={i}>
            <td>{i}</td>
            <td className="mono">{m.Word0}</td>
            <td className="mono">{fmtG(m.Floats123[0])}</td>
            <td className="mono">{fmtG(m.Floats123[1])}</td>
            <td className="mono">{fmtG(m.Floats123[2])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Structured-tail records — `Header.U19` × 96-byte records that lead
 *  the structured tail on every Fi02..Fi05 file. The framing (count /
 *  size / position) is Tier 3; per-word *meaning* is Tier 2 (no HW.exe
 *  display oracle), so the columns are deliberately labelled by word
 *  index rather than by inferred semantics. We surface the four
 *  highest-statistical-confidence words in dedicated columns and show
 *  the full 24-word record in a wrapped cell; hovering the row reveals
 *  each word reinterpreted as int32 / float32. */
export function EventsTab({ file }: { file: HwFile }) {
  if (file.TailRecords.length === 0)
    return (
      <div className="empty-state">
        {tr("(no tail records in this file)")}
      </div>
    );
  return (
    <>
      <p className="hint">
        {trf(
          "%s × 96-byte records.  Hover the W0..W23 cell to see each word reinterpreted as int32 / float32.",
          String(file.TailRecords.length),
        )}
      </p>
      <table>
        <thead>
          <tr>
            <th>{tr("#")}</th>
            <th title="Word 0 — uint32, monotonic across records (looks like a sample-index cursor; meaning unconfirmed)">
              {tr("Word0")}
            </th>
            <th title="Word 1 — uint32, also monotonic across records (independent cursor; meaning unconfirmed)">
              {tr("Word1")}
            </th>
            <th title="Word 10 — uint32, observed range 0..~10 (looks like a small enum; meaning unconfirmed)">
              {tr("Word10")}
            </th>
            <th title="Word 11 reinterpreted as float32 (looks like a sensible reading; meaning unconfirmed)">
              {tr("Word11 (f32)")}
            </th>
            <th>{tr("W0..W23 (uint32)")}</th>
          </tr>
        </thead>
        <tbody>
          {file.TailRecords.map((r) => (
            <tr key={r.Index} title={recordWordsAsAlt(r)}>
              <td>{r.Index}</td>
              <td className="mono">{r.Word0}</td>
              <td className="mono">{r.Word1}</td>
              <td className="mono">{r.Word10}</td>
              <td className="mono">{fmtG(r.Word11AsFloat)}</td>
              <td className="mono small">
                {Array.from(r.Words).join(" ")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function recordWordsAsAlt(r: DetectionEvent): string {
  const lines: string[] = [];
  for (let i = 0; i < r.Words.length; i++) {
    const u = r.Words[i] >>> 0;
    lines.push(
      `W${String(i).padStart(2, "0")}  u32=${u}  i32=${u32AsI32(u)}  f32=${fmtG(u32AsF32(u))}`,
    );
  }
  return lines.join("\n");
}

/** Layout tab — single tab that walks `file.Sections` in file-byte
 *  order. The parser is the SSOT for section offsets, lengths, labels,
 *  and tier annotations; the UI here just renders. Each section can
 *  expand to show a hex dump of its raw bytes (truncated to a
 *  configurable cap to keep big sections like the signal trace from
 *  blowing up the page). */
export function LayoutTab({ file }: { file: HwFile }) {
  return (
    <div className="report-section">
      <p className="hint">
        {trf(
          "%s sections, %s bytes total. Each row is one byte-range identified by the parser, in on-disk order. Click a row to reveal structured detail (when available) and a hex dump.",
          String(file.Sections.length),
          String(file.Sections.reduce((s, x) => s + x.Length, 0)),
        )}
      </p>
      <table>
        <thead>
          <tr>
            <th>{tr("Offset")}</th>
            <th>{tr("Length")}</th>
            <th>{tr("Tier")}</th>
            <th>{tr("Section")}</th>
          </tr>
        </thead>
        <tbody>
          {file.Sections.map((s) => (
            <SectionRow key={s.Offset} section={s} file={file} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

const HEX_PREVIEW_BYTES = 256;

/** Render the structured detail view for a section, if one exists. The
 *  dispatch table here is the *only* place that maps a section `Id` to
 *  its bespoke React view — `LayoutTab` is now the single drill-in
 *  surface for everything that used to live in standalone Header / Mid /
 *  TailRecords tabs. Returning `null` falls back to the hex preview. */
function structuredDetail(
  id: string,
  file: HwFile,
): React.ReactNode | null {
  switch (id) {
    case "header":
      return <HeaderTab file={file} />;
    case "mid-records":
      return <MidTab file={file} />;
    case "tail-records":
      return <EventsTab file={file} />;
    default:
      return null;
  }
}

function SectionRow({
  section,
  file,
}: {
  section: HwFile["Sections"][number];
  file: HwFile;
}) {
  const detail = structuredDetail(section.Id, file);
  return (
    <tr title={section.Note ?? ""}>
      <td className="mono">
        0x{section.Offset.toString(16).padStart(8, "0")}{" "}
        <span className="dim">({section.Offset})</span>
      </td>
      <td className="mono">{section.Length}</td>
      <td className={`tier tier-${section.Tier}`}>{section.Tier}</td>
      <td>
        <details>
          <summary>{section.Label}</summary>
          {detail ? <div className="section-detail">{detail}</div> : null}
          <details className="hex-disclosure">
            <summary className="dim small">{tr("Raw bytes")}</summary>
            <pre className="mono small hex-dump">
              {hex(section.Raw, HEX_PREVIEW_BYTES)}
              {section.Length > HEX_PREVIEW_BYTES
                ? trf(
                    "\n\n— %s of %s bytes shown —",
                    String(HEX_PREVIEW_BYTES),
                    String(section.Length),
                  )
                : ""}
            </pre>
          </details>
          {section.Note ? <p className="dim small">{section.Note}</p> : null}
        </details>
      </td>
    </tr>
  );
}

// --- numeric formatting helpers --------------------------------------------

/** Approximation of Go's `%.3g`. Used for raw header / event diagnostic
 *  cells where 3 significant figures is enough — never for HW.exe-faithful
 *  per-peak quantities (those go through the `fmt*` helpers in `parser.ts`). */
function fmtG(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  if (n === 0) return "0";
  return Number.parseFloat(n.toPrecision(3)).toString();
}

function fmtTime(d: Date): string {
  const p = (n: number) => (n < 10 ? "0" + n : "" + n);
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
    d.getHours(),
  )}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function signalRange(
  file: HwFile,
): { min: number; max: number } | null {
  const s = file.Signal;
  if (s.length === 0) return null;
  let mn = s[0];
  let mx = s[0];
  for (let i = 1; i < s.length; i++) {
    const v = s[i];
    if (v < mn) mn = v;
    if (v > mx) mx = v;
  }
  return { min: mn, max: mx };
}
